import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';

/**
 * Team management dialog — assign/remove staff, set lead, fire staff.
 */
export function openTeamManageDialog(parent: HTMLElement, store: GameStore, onClose: () => void): void {
  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const dialog = new Dialog({
    title: 'Manage Teams',
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
  });

  const container = document.createElement('div');
  container.style.cssText = 'display:flex;flex-direction:column;gap:10px;min-width:360px;max-height:550px;overflow-y:auto;';

  function render() {
    container.innerHTML = '';
    const state = store.getState();
    const theme = store.getThemeData();

    for (const team of state.teams) {
      const card = document.createElement('div');
      card.className = 'card';
      card.style.cssText = 'padding:10px;display:flex;flex-direction:column;gap:6px;';

      // Members
      const members = team.memberIds
        .map((id) => state.staff.find((s) => s.id === id))
        .filter((s): s is NonNullable<typeof s> => !!s);

      // Team header
      const header = document.createElement('div');
      header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;';
      const nameEl = document.createElement('strong');
      nameEl.textContent = `${team.name} (${members.length}/${team.maxSize})`;
      nameEl.style.fontSize = '13px';
      const statusEl = document.createElement('span');
      statusEl.style.cssText = 'font-size:10px;color:var(--color-text-muted);';
      statusEl.textContent = team.assignedProjectId ? 'Working on project' : members.length === 0 ? '⚠ Empty — add staff below' : 'Idle';
      header.appendChild(nameEl);
      header.appendChild(statusEl);
      card.appendChild(header);

      if (members.length === 0) {
        const empty = document.createElement('div');
        empty.textContent = 'No members';
        empty.style.cssText = 'font-size:11px;color:var(--color-text-muted);';
        card.appendChild(empty);
      }

      for (const member of members) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;justify-content:space-between;align-items:center;font-size:11px;padding:2px 0;border-bottom:1px solid var(--border-color);';

        const info = document.createElement('span');
        const roleDef = theme.entities.staffRoles.find((r) => r.id === member.role) ?? theme.entities.founderRole;
        const isLead = team.leaderId === member.id;
        info.innerHTML = `${member.name} <span style="color:var(--color-text-muted)">(${roleDef.name} Lv.${member.level})</span>${isLead ? ' <span style="color:var(--color-warning)">★ Lead</span>' : ''}`;

        const actions = document.createElement('div');
        actions.style.cssText = 'display:flex;gap:2px;';

        if (!isLead && members.length >= 2) {
          const leadBtn = document.createElement('button');
          leadBtn.className = 'btn btn--icon';
          leadBtn.textContent = '★';
          leadBtn.title = 'Set as lead';
          leadBtn.style.cssText = 'font-size:10px;min-width:22px;min-height:22px;padding:0;';
          leadBtn.addEventListener('click', () => {
            store.dispatch({ type: 'SET_TEAM_LEAD', teamId: team.id, staffId: member.id });
            render();
          });
          actions.appendChild(leadBtn);
        }

        if (!member.isCEO) {
          const removeBtn = document.createElement('button');
          removeBtn.className = 'btn btn--icon';
          removeBtn.textContent = '↩';
          removeBtn.title = 'Remove from team';
          removeBtn.style.cssText = 'font-size:10px;min-width:22px;min-height:22px;padding:0;';
          removeBtn.addEventListener('click', () => {
            store.dispatch({ type: 'REMOVE_FROM_TEAM', staffId: member.id });
            render();
          });
          actions.appendChild(removeBtn);

          const fireBtn = document.createElement('button');
          fireBtn.className = 'btn btn--icon';
          fireBtn.textContent = '✕';
          fireBtn.title = 'Fire';
          fireBtn.style.cssText = 'font-size:10px;min-width:22px;min-height:22px;padding:0;color:var(--color-danger);';
          fireBtn.addEventListener('click', () => {
            if (confirm(`Fire ${member.name}?`)) {
              store.dispatch({ type: 'FIRE_STAFF', staffId: member.id });
              render();
            }
          });
          actions.appendChild(fireBtn);
        }

        row.appendChild(info);
        row.appendChild(actions);
        card.appendChild(row);
      }

      // Add staff — only show unassigned staff
      const available = state.staff.filter(
        (s) => !s.isTraining && !s.isOnLeave && !s.teamId,
      );
      if (available.length > 0 && members.length < team.maxSize) {
        const addRow = document.createElement('div');
        addRow.style.cssText = 'display:flex;gap:4px;margin-top:4px;';

        const select = document.createElement('select');
        select.style.cssText = 'padding:4px;background:var(--color-bg-primary);border:1px solid var(--border-color);border-radius:3px;color:var(--color-text-primary);font-size:11px;flex:1;';
        for (const s of available) {
          const opt = document.createElement('option');
          opt.value = s.id;
          opt.textContent = `${s.name} (${s.role} Lv.${s.level})`;
          select.appendChild(opt);
        }

        const addBtn = document.createElement('button');
        addBtn.className = 'btn btn--success';
        addBtn.textContent = 'Add';
        addBtn.style.cssText = 'font-size:10px;padding:2px 8px;margin-left:4px;';
        addBtn.addEventListener('click', () => {
          const result = store.dispatch({ type: 'ASSIGN_TO_TEAM', staffId: select.value, teamId: team.id });
          if (!result.success) {
            console.warn('Assign failed:', result.error);
            const err = document.createElement('div');
            err.textContent = result.error ?? 'Failed';
            err.style.cssText = 'color:var(--color-danger);font-size:10px;margin-top:2px;';
            addRow.appendChild(err);
            setTimeout(() => err.remove(), 3000);
          }
          render();
        });

        addRow.appendChild(select);
        addRow.appendChild(addBtn);
        card.appendChild(addRow);
      }

      // Disband button — inside card, uses resolved members count (not raw memberIds)
      if (members.length === 0 && !team.assignedProjectId) {
        const disbandBtn = document.createElement('button');
        disbandBtn.className = 'btn';
        disbandBtn.textContent = `Disband ${team.name}`;
        disbandBtn.style.cssText = 'font-size:11px;color:var(--color-danger);margin-top:4px;';
        disbandBtn.addEventListener('click', () => {
          store.dispatch({ type: 'DISBAND_TEAM', teamId: team.id });
          render();
        });
        card.appendChild(disbandBtn);
      }

      container.appendChild(card);
    }

    if (state.teams.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No teams yet. Create one first!';
      empty.style.cssText = 'font-size:12px;color:var(--color-text-muted);text-align:center;padding:16px;';
      container.appendChild(empty);
    }
  }

  render();
  dialog.body.appendChild(container);
  dialog.mount(parent);
}
